import { rest } from 'app/rest'
import { getLang } from 'i18n/config/LanguageDetector'
import { items } from 'store/items'
import { me } from 'store/me'
import { opt } from 'store/opt'
import { AbuseCode } from 'type/Abuse'
import { Event } from 'type/Event'
import { CreateIdentityRequest, CreateIdentityResponse, IdentitiesRequest, Identity, UpdateIdentityRequest } from 'type/Identity'
import { IdentityConfig } from 'type/IdentityConfig'
import { GenerateRequest, ImageInferenceResponse, Inference, LikeMode, VideoInferenceResponse } from 'type/Inference'
import { Item, ItemRequest, SearchItemRequest } from 'type/Item'
import { ItemRecommendationsResponse } from 'type/ItemRecommendations'
import { Limit } from 'type/Limit'
import { MeResponse, UserUpdate } from 'type/Me'
import { Metrics } from 'type/metrics'
import { Options } from 'type/Options'
import { FiltersResponse, RecommendationsRequest, RecommendationsResponse } from 'type/rec'
import { BasicSizesRequest, DetailedSizesRequest, SizesResponse } from 'type/size'
import { TelegramResponse } from 'type/Telegram'
import { logUser } from 'util/adminDebug'

class Api {
  async getVersion(): Promise<string | undefined> {
    try {
      const { version } = await rest.get<any>('/api/version')
      return version || undefined
    } catch (e) {
      return undefined
    }
  }

  async getConfig(options: Options): Promise<Options> {
    const { product, client, company, access, preview } = options
    return await rest.get('/api/config', { product, client, company, access, preview }, { extend: false })
  }

  async getMe(force_: boolean, initData: string): Promise<MeResponse> {
    const user = me.user
    const force = force_ || undefined
    const hash = me.external && opt.external ? false : undefined
    const product = opt.options.product
    logUser('getMe request', { user, force, hash: hash ?? true, product: product ?? null })
    const result = await rest.post<MeResponse>('/api/me', { user, force, hash, product, initData })
    logUser('getMe response', { hasUser: !!result?.user, user_id: result?.user?.user_id ?? null })
    return result
  }

  async sendEvent(event: Event): Promise<void> {
    try {
      await rest.post('/api/events', { events: [event] })
    } catch {
    }
  }

  async updateUser(update: UserUpdate): Promise<void> {
    const { user_id } = me
    await rest.patch('/api/user', { user_id, ...update })
  }

  async download(url: string): Promise<Blob> {
    return await rest.post('/api/download', { url }, { extract: r => r.blob() })
  }

  async getItem(request: ItemRequest): Promise<Item> {
    const { item_id, shop } = request
    let item = items.get(item_id)
    if (item) return item
    item = await rest.get<Item>(`/api/items/${item_id}`, { shop })
    items.add(item)
    return item
  }

  async searchItem(request: SearchItemRequest): Promise<Item> {
    const { user_id } = me
    return await rest.post<Item>('/api/items/lookup', { ...request, user_id })
  }

  async getIdentityConfig(): Promise<IdentityConfig> {
    return await rest.get('/api/identity/config')
  }

  async uploadSelfie(selfie: File): Promise<string> {
    await me.ensure
    const { user_id } = me
    const [url] = await rest.form<string[]>('/api/photos/selfie', { user_id, selfie })
    if (!url) throw new Error('no url')
    return url
  }

  async uploadFullbody(image: File): Promise<string> {
    await me.ensure
    const { user_id } = me
    const [url] = await rest.form<string[]>('/api/photos/fullbody', { user_id, image })
    if (!url) throw new Error('no url')
    return url
  }

  async getIdentities(request: IdentitiesRequest): Promise<Identity[]> {
    return await rest.get('/api/identities', request)
  }

  async saveProfile(metrics: Metrics): Promise<void> {
    const { user_id } = me
    return await rest.patch('/api/metrics', { user_id, metrics })
  }

  async createIdentity(request: CreateIdentityRequest): Promise<CreateIdentityResponse> {
    return await rest.post('/api/identities', request)
  }

  async updateIdentity(request: UpdateIdentityRequest): Promise<unknown> {
    const { identity_id, ...data } = request
    return await rest.patch(`/api/identities/${identity_id}`, data)
  }

  async deleteIdentity(identity_id: string): Promise<unknown> {
    const { user_id } = me
    return await rest.del(`/api/identities/${identity_id}`, { user_id })
  }

  async checkLimit(item_id: string | undefined): Promise<Limit[]> {
    const { user_id } = me
    return await rest.get('/api/limits', { user_id, item_id })
  }

  async generateInference(request: GenerateRequest): Promise<ImageInferenceResponse> {
    return await rest.post('/api/inferences/image', request)
  }

  async generateVideo(source_inference_id: string): Promise<VideoInferenceResponse> {
    const { user_id } = me
    return await rest.post('/api/inferences/video', { user_id, source_inference_id })
  }

  async getInferences(user_id: string): Promise<Inference[]> {
    return await rest.get('/api/inferences', { user_id })
  }

  async getInference(inference_id: string): Promise<Inference> {
    return await rest.get(`/api/inferences/${inference_id}`)
  }

  async viewInference(inference_id: string): Promise<unknown> {
    const { user_id } = me
    return await rest.patch(`/api/inferences/${inference_id}`, { user_id })
  }

  async setInferenceImageLikeMode(image_id: number, like_mode: LikeMode): Promise<void> {
    await rest.put(`/api/images/${image_id}/like`, { like_mode })
  }

  async sendInferenceFeedback(image_id: number, code: AbuseCode, reason: string) {
    const { user_id } = me
    await rest.post(`/api/images/${image_id}/feedback`, { user_id, code, reason })
  }

  async shareInference(initData: string, inference_id: string): Promise<unknown> {
    const { user_id } = me
    const product = opt.options.product
    const lang = getLang()
    if (!product) throw new Error('no product')
    return await rest.post(`/api/inferences/${inference_id}/share`, { user_id, initData, product, lang })
  }

  async getRecommendations(request: RecommendationsRequest): Promise<RecommendationsResponse> {
    return await rest.post('/api/recommendations', request)
  }

  async getItemRecommendations(item_id: string): Promise<ItemRecommendationsResponse> {
    return await rest.get('/api/items/recommendations', { item_id })
  }

  async getFilters(combine: string, gender: string | undefined): Promise<FiltersResponse> {
    const group = opt.options.window?.group
    return await rest.get('/api/recommendations/filters', { group, combine, gender })
  }

  async setOrGetTelegramUserId(user_id: string, telegram_id: string): Promise<TelegramResponse> {
    return await rest.put(`/api/telegram/users/${telegram_id}`, { user_id })
  }

  async getBasicSizes(request: BasicSizesRequest): Promise<SizesResponse> {
    return await rest.post<SizesResponse>('/api/sizes/basic', request)
  }

  async getDetailedSizes(request: DetailedSizesRequest): Promise<SizesResponse> {
    return await rest.post<SizesResponse>('/api/sizes/detailed', request)
  }
}

export const api = new Api()
